import React, { useEffect, useRef, useState } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';

const DEFAULT_SUCCESS_TEXT = 'Saved ✓';
const DEFAULT_ERROR_TEXT = 'Try again';
const DEFAULT_LABEL = 'Save';
const DEFAULT_SUCCESS_DURATION = 1400;
const DEFAULT_ERROR_DURATION = 1400;

const isPromiseLike = (value) => Boolean(value && typeof value.then === 'function');

const SaveButton = ({
  onClick,
  label = DEFAULT_LABEL,
  loading: loadingProp,
  status: statusProp,
  successText = DEFAULT_SUCCESS_TEXT,
  errorText = DEFAULT_ERROR_TEXT,
  successDuration = DEFAULT_SUCCESS_DURATION,
  errorDuration = DEFAULT_ERROR_DURATION,
  disabled = false,
  children,
  onError,
  sx = {},
  startIcon,
  ...props
}) => {
  const [internalStatus, setInternalStatus] = useState('idle');
  const resetTimerRef = useRef(null);

  useEffect(() => () => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
    }
  }, []);

  const controlledStatus = statusProp || null;
  const effectiveStatus = controlledStatus || internalStatus || (loadingProp === true ? 'saving' : 'idle');
  const isSaving = effectiveStatus === 'saving';
  const isSuccess = effectiveStatus === 'success';
  const isError = effectiveStatus === 'error';
  const idleLabel = children || label;
  const displayedLabel = isSaving
    ? 'Saving...'
    : isSuccess
      ? successText
      : isError
        ? errorText
        : idleLabel;

  const clearResetTimer = () => {
    if (resetTimerRef.current) {
      window.clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  };

  const scheduleIdleReset = () => {
    clearResetTimer();
    resetTimerRef.current = window.setTimeout(() => {
      setInternalStatus('idle');
      resetTimerRef.current = null;
    }, successDuration);
  };

  const scheduleErrorReset = () => {
    clearResetTimer();
    resetTimerRef.current = window.setTimeout(() => {
      setInternalStatus('idle');
      resetTimerRef.current = null;
    }, errorDuration);
  };

  const handleClick = async (event) => {
    if (disabled || isSaving) {
      return;
    }

    if (!statusProp) {
      clearResetTimer();
      setInternalStatus('saving');
    }

    try {
      const result = onClick?.(event);
      if (isPromiseLike(result)) {
        await result;
      }

      if (!statusProp) {
        setInternalStatus('success');
        scheduleIdleReset();
      }
    } catch (error) {
      if (!statusProp) {
        setInternalStatus('error');
        scheduleErrorReset();
      }
      onError?.(error);
    }
  };

  return (
    <Button
      {...props}
      onClick={handleClick}
      disabled={disabled || isSaving}
      startIcon={
        isSaving ? (
          <CircularProgress size={16} color="inherit" />
        ) : startIcon
      }
      aria-live="polite"
      sx={{
        minHeight: 44,
        textTransform: 'none',
        transition: 'transform 150ms ease, background-color 150ms ease, border-color 150ms ease, box-shadow 150ms ease',
        touchAction: 'manipulation',
        '&:active': {
          transform: 'scale(0.98)',
        },
        ...sx,
      }}
    >
      <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
        {displayedLabel}
      </Box>
    </Button>
  );
};

export default SaveButton;
